import { 
  createIcons, 
  FolderKanban, 
  Plus, 
  Search, 
  Filter, 
  Calendar, 
  User, 
  Layers, 
  Eye, 
  Edit3, 
  Trash2, 
  Building2, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  ExternalLink,
  ChevronDown,
  ChevronRight,
  GitBranch,
  Boxes,
  Power,
  Link,
  Check,
  X,
  FileText
} from "lucide";
import { router } from "../router/router.js";
import { contextStore } from "../auth/context-store.js";
import { sessionRequestContext } from "../auth/request-context.js";
import type { Project, ProjectUnitArea, ProjectStatus, UnitAreaStatus } from "../../domain/project/project-domain.js";
import {
  clientOptionsForNewProject,
  formatClientLabel,
  formatWorkOrderOptionLabel,
  keepWorkOrderOnClientChange,
  restrictWorkOrdersToClients,
  workOrderOptionsForClient,
  NO_WORK_ORDERS_FOR_CLIENT_MESSAGE,
  SELECT_CLIENT_FIRST_PLACEHOLDER
} from "../../domain/project/project-work-order-cascade.js";
import type { WorkOrderOptionLike } from "../../domain/project/project-work-order-cascade.js";

export class AppProjectsPage extends HTMLElement {
  private projects: Project[] = [];
  private unitAreas: ProjectUnitArea[] = [];
  private pendingUnitAreas: ProjectUnitArea[] = [];
  private clients: any[] = [];
  private equipments: any[] = [];
  private workOrders: WorkOrderOptionLike[] = [];
  /** Vínculos OS ↔ Projeto: um Projeto pode estar em mais de uma OS. */
  private workOrderProjectLinks: { workOrderId: string; projectId: string }[] = [];
  private isLoading = true;
  private searchTerm = "";
  private clientFilter = "ALL";
  private statusFilter = "ALL";
  private selectedProjectId: string | null = null;
  private expandedProjectIds: Set<string> = new Set();
  private unsubscribe: (() => void) | null = null;
  private boundHandleMasterDataUpdated: (e: Event) => void;

  constructor() {
    super();
    this.boundHandleMasterDataUpdated = this.handleMasterDataUpdated.bind(this);
  }

  private handleMasterDataUpdated(e: Event) {
    this.loadData();
  }

  connectedCallback() {
    // Sincroniza filtro inicial com o cliente ativo se disponível
    const activeClientId = contextStore.get().activeClientId;
    if (activeClientId) {
      this.clientFilter = activeClientId;
    }

    this.loadData();
    this.unsubscribe = contextStore.subscribe(() => {
      const currentActiveClient = contextStore.get().activeClientId;
      if (currentActiveClient && this.clientFilter !== currentActiveClient && this.clientFilter !== 'ALL') {
        this.clientFilter = currentActiveClient;
      }
      this.loadData();
    });

    window.addEventListener('MASTER_DATA_UPDATED', this.boundHandleMasterDataUpdated);
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
    window.removeEventListener('MASTER_DATA_UPDATED', this.boundHandleMasterDataUpdated);
  }

  private async loadData() {
    this.isLoading = true;
    this.render();

    try {
      const activeClientId = contextStore.get().activeClientId;
      const dummyContext = { activeClientId, permissions: contextStore.get().permissions, correlationId: '', userId: contextStore.get().userId } as any;

      const [prjs, uas, pendingUas, cls, eqs, wos] = await Promise.all([
        window.app.platform.projectService.listProjects(undefined, undefined, sessionRequestContext()),
        window.app.platform.projectService.listUnitAreas(undefined, undefined, sessionRequestContext()),
        window.app.platform.projectService.listPendingUnitAreas ? window.app.platform.projectService.listPendingUnitAreas(undefined, sessionRequestContext()) : Promise.resolve([]),
        window.app.platform.adminService.listAuthorizedClients(dummyContext).catch(() => []),
        window.app.platform.equipmentService.listEquipments(dummyContext).catch(() => []),
        // A cascata do formulário segue o Cliente ESCOLHIDO, não o Cliente
        // global: por isso as OS vêm sem o filtro de contexto e são depois
        // restritas aos Clientes autorizados do usuário.
        window.app.platform.adminService
          .listWorkOrders({ ...dummyContext, activeClientId: '' })
          .catch((e: unknown) => {
            console.error('[PROJETOS] Falha ao carregar as Ordens de Serviço', e);
            return [];
          })
      ]);

      this.projects = prjs || [];
      this.unitAreas = uas || [];
      this.pendingUnitAreas = pendingUas || [];
      this.clients = cls || [];
      this.equipments = eqs || [];
      this.workOrders = restrictWorkOrdersToClients((wos || []) as WorkOrderOptionLike[], this.clients);
      this.workOrderProjectLinks = await window.app.platform.rdoService
        .listWorkOrderProjectLinks({ ...dummyContext, activeClientId: '' })
        .catch((e: unknown) => {
          console.error('[PROJETOS] Falha ao carregar os vínculos OS ↔ Projeto', e);
          return [];
        });

      // Se todos os projetos ainda não foram expandidos, expande os primeiros 5 por padrão
      if (this.expandedProjectIds.size === 0 && this.projects.length > 0) {
        this.projects.slice(0, 5).forEach(p => this.expandedProjectIds.add(p.id));
      }

      // Se havia um projeto selecionado que não existe mais, limpa a seleção
      if (this.selectedProjectId && !this.projects.some(p => p.id === this.selectedProjectId)) {
        this.selectedProjectId = null;
      }

      this.isLoading = false;
      this.render();
    } catch (e: any) {
      this.isLoading = false;
      this.renderError(e.message);
    }
  }

  private getEffectiveClientId(): string | null {
    if (this.clientFilter && this.clientFilter !== 'ALL') {
      return this.clientFilter;
    }
    return contextStore.get().activeClientId || (this.clients[0]?.id || null);
  }

  private getFilteredProjects(): Project[] {
    return this.projects.filter(p => {
      const matchesClient = this.clientFilter === 'ALL' || p.clientId === this.clientFilter;
      const matchesStatus = this.statusFilter === 'ALL' || p.status === this.statusFilter;
      const term = this.searchTerm.toLowerCase().trim();
      const matchesSearch = !term ||
        p.code.toLowerCase().includes(term) ||
        p.name.toLowerCase().includes(term) ||
        (p.description && p.description.toLowerCase().includes(term)) ||
        (p.manager && p.manager.toLowerCase().includes(term)) ||
        // Busca também pelas unidades filhas
        this.unitAreas.some(u => u.projectId === p.id && (
          u.name.toLowerCase().includes(term) ||
          u.code.toLowerCase().includes(term)
        ));

      return matchesClient && matchesStatus && matchesSearch;
    });
  }

  private getStatusBadge(status: ProjectStatus): string {
    switch (status) {
      case 'ACTIVE':
        return `<span style="background: rgba(34, 197, 94, 0.15); color: var(--status-success-text); border: 1px solid rgba(34, 197, 94, 0.3); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 5px;"><span style="width: 6px; height: 6px; border-radius: 50%; background: #4ade80;"></span> ATIVO</span>`;
      case 'PLANNING':
        return `<span style="background: rgba(59, 130, 246, 0.15); color: var(--ia-info-fg, #60a5fa); border: 1px solid rgba(59, 130, 246, 0.3); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 5px;"><span style="width: 6px; height: 6px; border-radius: 50%; background: #60a5fa;"></span> PLANEJAMENTO</span>`;
      case 'COMPLETED':
        return `<span style="background: rgba(168, 85, 247, 0.15); color: var(--ia-info-fg, #c084fc); border: 1px solid rgba(168, 85, 247, 0.3); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 5px;"><span style="width: 6px; height: 6px; border-radius: 50%; background: #c084fc;"></span> CONCLUÍDO</span>`;
      case 'CANCELLED':
        return `<span style="background: rgba(239, 68, 68, 0.15); color: var(--status-danger-solid); border: 1px solid rgba(239, 68, 68, 0.3); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 600; display: inline-flex; align-items: center; gap: 5px;"><span style="width: 6px; height: 6px; border-radius: 50%; background: var(--status-danger-solid);"></span> INATIVO</span>`;
      default:
        return `<span style="background: rgba(140, 140, 140, 0.15); color: var(--ia-fg-soft, #b5b5b5); padding: 2px 8px; border-radius: 4px; font-size: 0.75rem;">${status}</span>`;
    }
  }

  private getUnitStatusBadge(status: UnitAreaStatus): string {
    if (status === 'ACTIVE') {
      return `<span style="background: rgba(34, 197, 94, 0.12); color: var(--status-success-border); border: 1px solid rgba(34, 197, 94, 0.25); padding: 2px 6px; border-radius: 4px; font-size: 0.72rem; font-weight: 600;">ATIVO</span>`;
    }
    return `<span style="background: rgba(239, 68, 68, 0.12); color: var(--status-danger-border); border: 1px solid rgba(239, 68, 68, 0.25); padding: 2px 6px; border-radius: 4px; font-size: 0.72rem; font-weight: 600;">INATIVO</span>`;
  }

  private escapeHtml(str?: string | null): string {
    if (!str) return '';
    return str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  private renderError(msg: string) {
    this.innerHTML = `
      <div style="padding: 32px; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 8px; margin: 24px; color: var(--status-danger-solid);">
        <h3 style="margin: 0 0 8px 0; font-size: 1.1rem;">Erro ao carregar Projetos</h3>
        <p style="margin: 0 0 16px 0; color: var(--color-text-disabled);">${msg}</p>
        <button id="btn-retry" style="background: var(--color-primary); color: var(--color-text-primary); border: none; padding: 8px 16px; border-radius: 6px; cursor: pointer;">Tentar novamente</button>
      </div>
    `;
    this.querySelector('#btn-retry')?.addEventListener('click', () => this.loadData());
  }

  private render() {
    if (this.isLoading) {
      this.innerHTML = `
        <div style="padding: 72px 24px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; color: var(--color-text-muted);">
          <svg class="spinner spinner-dark spinner-lg" viewBox="0 0 24 24" fill="none" aria-label="Carregando...">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-opacity="0.2" stroke-width="3"/>
            <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
          </svg>
          <span class="text-body-small">Carregando estrutura hierárquica de Projetos e Unidades/Áreas...</span>
        </div>
      `;
      return;
    }

    const filteredProjects = this.getFilteredProjects();
    const selectedProject = this.selectedProjectId ? this.projects.find(p => p.id === this.selectedProjectId) : null;
    const activeClient = this.clients.find(c => c.id === this.getEffectiveClientId());
    const clientDisplayName = activeClient ? (activeClient.name || activeClient.legalName) : (this.clientFilter === 'ALL' ? 'Todos os Clientes' : 'Cliente Ativo');

    // Filtra pendentes de acordo com o cliente filtrado se aplicável
    const visiblePendingUnits = this.pendingUnitAreas.filter(u => this.clientFilter === 'ALL' || u.clientId === this.clientFilter);

    this.innerHTML = `
      <style>
        .prj-module-container { padding: 24px; max-width: 1440px; margin: 0 auto; color: var(--color-text-primary); font-family: system-ui, -apple-system, sans-serif; }
        .prj-header-bar { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; flex-wrap: wrap; gap: 16px; border-bottom: 1px solid #27272a; padding-bottom: 16px; }
        .prj-title-box h1 { margin: 0 0 6px 0; font-size: 1.65rem; font-weight: 700; color: var(--color-text-primary); display: flex; align-items: center; gap: 10px; }
        .prj-hierarchy-breadcrumb { font-size: 0.8125rem; color: var(--color-text-secondary); display: flex; align-items: center; gap: 6px; background: rgba(59, 130, 246, 0.1); border: 1px solid rgba(59, 130, 246, 0.25); padding: 4px 10px; border-radius: 6px; width: fit-content; }
        
        .prj-header-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .btn-new-prj { background: var(--color-primary); color: var(--color-text-primary); border: none; padding: 9px 18px; border-radius: 6px; font-size: 0.875rem; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; transition: background 0.2s; box-shadow: 0 1px 3px rgba(0,0,0,0.3); }
        .btn-new-prj:hover { background: var(--color-primary); }
        .btn-new-unit { background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); border: none; padding: 9px 18px; border-radius: 6px; font-size: 0.875rem; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; transition: background 0.2s; box-shadow: 0 1px 3px rgba(0,0,0,0.3); }
        .btn-new-unit:hover { filter: brightness(1.1); }
        
        /* Banner de Projeto Selecionado (Regra 5) */
        .prj-selected-banner { background: rgba(37, 99, 235, 0.12); border: 1px solid rgba(59, 130, 246, 0.35); border-radius: 8px; padding: 12px 18px; margin-bottom: 20px; display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; }
        
        /* Toolbar */
        .prj-filter-toolbar { display: flex; gap: 10px; margin-bottom: 20px; flex-wrap: wrap; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--ia-line, #27272a); border-radius: 8px; padding: 12px 16px; align-items: center; }
        .prj-search-wrap { flex: 1; min-width: 260px; position: relative; }
        .prj-search-wrap input { width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px 8px 36px; color: var(--color-text-primary); font-size: 0.875rem; }
        .prj-search-wrap input:focus { border-color: var(--color-ink); outline: none; }
        .prj-search-wrap i { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); color: var(--color-text-primary); }
        .prj-filter-select { background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; color: var(--color-text-primary); font-size: 0.875rem; min-width: 180px; }
        
        /* Árvore Hierárquica */
        .prj-tree-container { display: flex; flex-direction: column; gap: 14px; }
        .prj-card { background: var(--ia-bg-panel, #18181b); border: 1px solid var(--ia-line, #27272a); border-radius: 8px; overflow: hidden; transition: border-color 0.2s, box-shadow 0.2s; }
        .prj-card.selected-focus { border-color: var(--color-ink); box-shadow: 0 0 0 1px var(--color-ink); }
        
        .prj-card-header { padding: 14px 18px; background: var(--ia-bg-panel, #1e1e22); border-bottom: 1px solid transparent; display: flex; justify-content: space-between; align-items: center; cursor: pointer; user-select: none; gap: 14px; flex-wrap: wrap; }
        .prj-card.expanded .prj-card-header { border-bottom-color: #27272a; background: var(--ia-bg-panel, #222227); }
        
        .prj-header-left { display: flex; align-items: center; gap: 12px; flex: 1; min-width: 280px; }
        .prj-toggle-btn { background: var(--ia-bg-panel, #27272a); border: 1px solid var(--color-input-border); color: var(--color-text-disabled); width: 28px; height: 28px; border-radius: 6px; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; }
        .prj-toggle-btn:hover { color: var(--color-text-primary); background: var(--ia-bg-raised, #3f3f46); }
        
        .prj-code-badge { font-family: monospace; font-size: 0.85rem; font-weight: 700; color: var(--ia-info-fg, #60a5fa); background: rgba(59, 130, 246, 0.12); padding: 3px 8px; border-radius: 5px; border: 1px solid rgba(59, 130, 246, 0.25); white-space: nowrap; }
        .prj-name-title { font-size: 1.05rem; font-weight: 600; color: var(--color-text-primary); }
        
        .prj-metrics-badge { font-size: 0.78rem; background: var(--ia-bg-panel, #27272a); color: var(--color-graphite-300); padding: 3px 9px; border-radius: 12px; display: inline-flex; align-items: center; gap: 5px; font-weight: 500; }
        .prj-metrics-badge strong { color: var(--color-text-primary); }
        
        .prj-actions-bar { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
        .btn-sub-action { background: var(--ia-bg-panel, #27272a); border: 1px solid var(--color-input-border); color: var(--color-text-primary); padding: 5px 10px; border-radius: 5px; font-size: 0.78rem; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; transition: all 0.15s; }
        .btn-sub-action:hover { background: var(--ia-bg-raised, #3f3f46); color: var(--color-text-primary); }
        .btn-sub-action.primary { background: rgba(59, 130, 246, 0.15); border-color: rgba(59, 130, 246, 0.4); color: var(--color-border-default); }
        .btn-sub-action.primary:hover { background: var(--color-primary); color: var(--color-text-primary); }
        .btn-sub-action.success { background: rgba(16, 185, 129, 0.15); border-color: rgba(16, 185, 129, 0.4); color: var(--status-success-border); }
        .btn-sub-action.success:hover { background: var(--status-success-text); color: var(--color-text-primary); }
        .btn-sub-action.danger:hover { background: rgba(239, 68, 68, 0.2); border-color: var(--status-danger-solid); color: var(--status-danger-border); }
        
        /* Lista de Unidades/Áreas do Projeto */
        .prj-units-tree { padding: 16px 20px; background: var(--ia-bg-panel, #121215); }
        .prj-tree-title { font-size: 0.78rem; font-weight: 700; text-transform: uppercase; color: var(--color-graphite-400); letter-spacing: 0.05em; margin-bottom: 12px; display: flex; align-items: center; justify-content: space-between; }
        
        .unit-tree-node { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; background: var(--ia-bg-panel, #18181b); border: 1px solid var(--ia-line, #27272a); border-radius: 6px; margin-bottom: 8px; transition: background 0.15s; position: relative; }
        .unit-tree-node:hover { background: var(--ia-bg-panel, #202025); border-color: var(--ia-line, #3f3f46); }
        .unit-tree-node::before { content: ""; position: absolute; left: -14px; top: 50%; width: 12px; height: 1px; background: var(--ia-bg-raised, #3f3f46); }
        
        .unit-left-info { display: flex; align-items: center; gap: 12px; flex: 1; }
        .unit-branch-icon { font-family: monospace; color: var(--ia-info-fg, #60a5fa); font-size: 0.95rem; user-select: none; }
        .unit-code-pill { font-family: monospace; font-size: 0.78rem; font-weight: 700; color: var(--color-border-default); background: rgba(99, 102, 241, 0.12); border: 1px solid rgba(99, 102, 241, 0.25); padding: 2px 6px; border-radius: 4px; }
        .unit-name-text { font-size: 0.92rem; font-weight: 600; color: var(--color-bg-secondary); }
        .unit-desc-text { font-size: 0.75rem; color: var(--color-graphite-400); margin-left: 8px; }
        
        .unit-right-actions { display: flex; align-items: center; gap: 6px; }
      </style>

      <div class="prj-module-container">
        <!-- 1. TOPO / CABEÇALHO -->
        <div class="prj-header-bar">
          <div class="prj-title-box">
            <h1>
              <i data-lucide="folder-kanban" style="color: var(--ia-info-fg, #60a5fa); width: 32px; height: 32px;"></i>
              Projetos
            </h1>
            <div class="prj-hierarchy-breadcrumb">
              <i data-lucide="git-branch" style="width: 14px; height: 14px;"></i>
              <span>Estrutura Corporativa: <strong>${this.escapeHtml(clientDisplayName)}</strong> &rarr; Projetos &rarr; Unidades/Áreas &rarr; Equipamentos</span>
            </div>
          </div>

          <div class="prj-header-actions">
            <button class="btn-sub-action" id="btn-goto-work-orders" style="padding: 8px 14px; font-size: 0.85rem; font-weight: 600; color: var(--color-text-muted); border-color: rgba(56, 189, 248, 0.4); background: rgba(56, 189, 248, 0.1); cursor: pointer;" title="Ir para a aba de Ordens de Serviço (OS)">
              <i data-lucide="file-text" style="width: 15px; height: 15px;"></i> Ordens de Serviço (OS)
            </button>
            <button class="btn-new-prj" id="btn-new-project">
              <i data-lucide="plus" style="width: 16px; height: 16px;"></i> Novo Projeto
            </button>
            <button class="btn-new-unit" id="btn-new-unit-area">
              <i data-lucide="plus" style="width: 16px; height: 16px;"></i> Nova Unidade/Área
            </button>
          </div>
        </div>

        <!-- 2. BANNER DE PROJETO SELECIONADO (REGRA 5) -->
        ${selectedProject ? `
          <div class="prj-selected-banner">
            <div style="display: flex; align-items: center; gap: 10px;">
              <span style="background: var(--color-primary); color: var(--color-text-primary); padding: 4px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 700; text-transform: uppercase;">Projeto em Foco</span>
              <span style="font-size: 0.95rem; color: var(--color-text-primary);">
                <strong>${this.escapeHtml(selectedProject.code)}</strong> - ${this.escapeHtml(selectedProject.name)}
              </span>
              <span style="font-size: 0.8rem; color: var(--color-border-default);">(${this.unitAreas.filter(u => u.projectId === selectedProject.id).length} unidades cadastradas)</span>
            </div>
            <div style="display: flex; gap: 8px; align-items: center;">
              <button class="btn-sub-action success" id="btn-add-unit-to-selected" data-id="${selectedProject.id}">
                <i data-lucide="plus" style="width: 14px; height: 14px;"></i> + Nova Unidade/Área neste Projeto
              </button>
              <button class="btn-sub-action" id="btn-clear-selection" title="Desmarcar seleção">
                <i data-lucide="x" style="width: 14px; height: 14px;"></i> Desmarcar
              </button>
            </div>
          </div>
        ` : ''}

        <!-- 3. AVISO DE ÁREAS PENDENTES DE VINCULAÇÃO A PROJETO (REGRA 13) -->
        ${visiblePendingUnits.length > 0 ? `
          <div style="background: rgba(245, 158, 11, 0.1); border: 1px solid rgba(245, 158, 11, 0.35); border-radius: 8px; padding: 14px 18px; margin-bottom: 20px;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
              <div style="display: flex; align-items: center; gap: 8px; color: var(--status-warning-text); font-weight: 700; font-size: 0.92rem;">
                <i data-lucide="alert-circle" style="width: 18px; height: 18px;"></i>
                <span>Atenção: Existem ${visiblePendingUnits.length} Unidade(s)/Área(s) Pendentes de Vinculação a um Projeto</span>
              </div>
              <span style="font-size: 0.78rem; color: var(--status-warning-border);">Migração de dados legados</span>
            </div>
            <p style="margin: 0 0 12px 0; font-size: 0.82rem; color: var(--color-graphite-300); line-height: 1.4;">
              Pela hierarquia oficial <strong>CLIENTE &rarr; PROJETO &rarr; UNIDADE/ÁREA</strong>, nenhuma unidade pode existir desvinculada de um projeto. Associe as unidades abaixo ao projeto correspondente:
            </p>
            <div style="display: flex; flex-direction: column; gap: 8px;">
              ${visiblePendingUnits.map(pu => `
                <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 8px 12px; display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap;">
                  <div style="display: flex; align-items: center; gap: 8px;">
                    <span style="font-family: monospace; font-size: 0.8rem; color: var(--status-warning-text); font-weight: 700;">${this.escapeHtml(pu.code || 'S/C')}</span>
                    <span style="font-weight: 600; color: var(--color-text-primary); font-size: 0.88rem;">${this.escapeHtml(pu.name)}</span>
                    ${pu.description ? `<span style="font-size: 0.75rem; color: var(--color-graphite-400);">(${this.escapeHtml(pu.description)})</span>` : ''}
                  </div>
                  <div style="display: flex; align-items: center; gap: 8px;">
                    <select class="prj-filter-select sel-bind-project" data-unit-id="${pu.id}" style="padding: 6px 10px; font-size: 0.8rem; min-width: 220px;">
                      <option value="">Selecione o Projeto de Destino...</option>
                      ${this.projects.filter(p => this.clientFilter === 'ALL' || p.clientId === this.clientFilter).map(p => `
                        <option value="${p.id}">${this.escapeHtml(p.code)} - ${this.escapeHtml(p.name)}</option>
                      `).join('')}
                    </select>
                    <button class="btn-sub-action primary btn-execute-bind" data-unit-id="${pu.id}">
                      <i data-lucide="link" style="width: 13px; height: 13px;"></i> Vincular
                    </button>
                  </div>
                </div>
              `).join('')}
            </div>
          </div>
        ` : ''}

        <!-- 4. TOOLBAR DE FILTROS E BUSCA -->
        <div class="prj-filter-toolbar">
          <div class="prj-search-wrap">
            <i data-lucide="search" style="width: 16px; height: 16px;"></i>
            <input type="text" id="prj-search-input" placeholder="Buscar por código, nome de projeto ou unidade/área..." value="${this.escapeHtml(this.searchTerm)}">
          </div>

          <select class="prj-filter-select" id="prj-client-filter">
            <option value="ALL" ${this.clientFilter === 'ALL' ? 'selected' : ''}>Todos os Clientes</option>
            ${this.clients.map(c => `<option value="${c.id}" ${this.clientFilter === c.id ? 'selected' : ''}>${this.escapeHtml(c.name || c.legalName)}</option>`).join('')}
          </select>

          <select class="prj-filter-select" id="prj-status-filter">
            <option value="ALL" ${this.statusFilter === 'ALL' ? 'selected' : ''}>Todos os Status</option>
            <option value="ACTIVE" ${this.statusFilter === 'ACTIVE' ? 'selected' : ''}>Ativo</option>
            <option value="PLANNING" ${this.statusFilter === 'PLANNING' ? 'selected' : ''}>Em Planejamento</option>
            <option value="COMPLETED" ${this.statusFilter === 'COMPLETED' ? 'selected' : ''}>Concluído</option>
            <option value="CANCELLED" ${this.statusFilter === 'CANCELLED' ? 'selected' : ''}>Inativo / Cancelado</option>
          </select>

          <div style="display: flex; gap: 6px; margin-left: auto;">
            <button class="btn-sub-action" id="btn-expand-all" title="Expandir todos os projetos">
              <i data-lucide="chevron-down" style="width: 14px; height: 14px;"></i> Expandir Todos
            </button>
            <button class="btn-sub-action" id="btn-collapse-all" title="Recolher todos os projetos">
              <i data-lucide="chevron-right" style="width: 14px; height: 14px;"></i> Recolher Todos
            </button>
          </div>
        </div>

        <!-- 5. VISUALIZAÇÃO HIERÁRQUICA (REGRA 6) -->
        <div class="prj-tree-container">
          ${filteredProjects.length === 0 ? `
            <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--ia-line, #27272a); border-radius: 8px; padding: 48px 20px; text-align: center; color: var(--color-text-primary);">
              <i data-lucide="folder-kanban" style="width: 42px; height: 42px; margin-bottom: 12px; ; color: var(--ia-info-fg, #60a5fa);"></i>
              <h3 style="margin: 0 0 6px 0; font-size: 1.05rem; color: var(--color-text-primary);">Nenhum projeto encontrado</h3>
              <p style="margin: 0 0 16px 0; font-size: 0.85rem; color: var(--color-text-disabled);">
                ${this.searchTerm ? 'Nenhum projeto ou unidade corresponde ao termo de busca.' : 'Cadastre o primeiro projeto do cliente para iniciar a árvore de unidades e equipamentos.'}
              </p>
              <button class="btn-new-prj" id="btn-empty-new-prj" style="margin: 0 auto;">
                <i data-lucide="plus" style="width: 16px; height: 16px;"></i> + Cadastrar Projeto Agora
              </button>
            </div>
          ` : filteredProjects.map(p => {
            const isExpanded = this.expandedProjectIds.has(p.id);
            const isSelected = this.selectedProjectId === p.id;
            const projectUnits = this.unitAreas.filter(u => u.projectId === p.id);
            const projectEquipments = this.equipments.filter(e => e.projectId === p.id);
            // As OS do Projeto vêm dos vínculos OS ↔ Projeto, não só do campo
            // legado da OS — senão um Projeto vinculado pela entidade formal
            // apareceria com zero OS.
            const projectWorkOrders = this.workOrdersOfProject(p.id);
            const client = this.clients.find(c => c.id === p.clientId);
            const clientName = client ? (client.name || client.legalName) : ((p as any).clientName || 'Cliente');

            return `
              <div class="prj-card ${isExpanded ? 'expanded' : ''} ${isSelected ? 'selected-focus' : ''}" data-project-id="${p.id}">
                <!-- CABEÇALHO DO PROJETO -->
                <div class="prj-card-header btn-toggle-expand" data-id="${p.id}">
                  <div class="prj-header-left">
                    <button class="prj-toggle-btn" title="${isExpanded ? 'Recolher' : 'Expandir'}">
                      <i data-lucide="${isExpanded ? 'chevron-down' : 'chevron-right'}" style="width: 16px; height: 16px;"></i>
                    </button>
                    
                    <span class="prj-code-badge">${this.escapeHtml(p.code)}</span>
                    
                    <div>
                      <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
                        <span class="prj-name-title">${this.escapeHtml(p.name)}</span>
                        ${this.getStatusBadge(p.status)}
                        ${isSelected ? `<span style="background: var(--color-primary); color: var(--color-text-primary); font-size: 0.7rem; font-weight: 700; padding: 2px 6px; border-radius: 4px;">SELECIONADO</span>` : ''}
                      </div>
                      <div style="font-size: 0.78rem; color: var(--color-graphite-400); margin-top: 3px; display: flex; gap: 12px; align-items: center; flex-wrap: wrap;">
                        <span><i data-lucide="building-2" style="width: 1em; height: 1em; display: inline-block; vertical-align: middle; margin-top: -2px; margin-right: 4px;"></i> ${this.escapeHtml(clientName)}</span>
                        <span title="Ordem de Serviço deste Projeto"><i data-lucide="file-text" style="width: 1em; height: 1em; display: inline-block; vertical-align: middle; margin-top: -2px; margin-right: 4px;"></i> ${
                          projectWorkOrders.length === 0
                            ? '<span style="color: var(--status-warning-solid);">Sem OS vinculada</span>'
                            : this.escapeHtml(projectWorkOrders.map(wo => wo.code).join(', '))
                        }</span>
                        ${p.startDate || p.expectedEndDate ? `<span title="Período do Projeto"><i data-lucide="calendar" style="width: 1em; height: 1em; display: inline-block; vertical-align: middle; margin-top: -2px; margin-right: 4px;"></i> ${p.startDate ? new Date(p.startDate + 'T00:00:00').toLocaleDateString('pt-BR') : '—'} → ${p.expectedEndDate ? new Date(p.expectedEndDate + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}</span>` : ''}
                        ${p.manager ? `<span><i data-lucide="user" style="width: 1em; height: 1em; display: inline-block; vertical-align: middle; margin-top: -2px; margin-right: 4px;"></i> ${this.escapeHtml(p.manager)}</span>` : ''}
                        ${p.description ? `<span style="color: var(--color-text-primary);">— ${this.escapeHtml(p.description)}</span>` : ''}
                      </div>
                    </div>
                  </div>

                  <div style="display: flex; align-items: center; gap: 14px;" onclick="event.stopPropagation()">
                    <div style="display: flex; gap: 6px;">
                      <span class="prj-metrics-badge" title="Unidades/Áreas vinculadas a este projeto">
                        <i data-lucide="layers" style="width: 13px; height: 13px; color: var(--ia-info-fg, #60a5fa);"></i>
                        <strong>${projectUnits.length}</strong> Unidades
                      </span>
                      <span class="prj-metrics-badge" title="Equipamentos ativos cadastrados neste projeto">
                        <i data-lucide="boxes" style="width: 13px; height: 13px; color: var(--status-success-text);"></i>
                        <strong>${projectEquipments.length}</strong> Ativos
                      </span>
                      <span class="prj-metrics-badge btn-view-project-os" data-id="${p.id}" data-client-id="${p.clientId}" title="Ordens de Serviço vinculadas a este projeto (Clique para ver na aba de OS)" style="cursor: pointer;">
                        <i data-lucide="file-text" style="width: 13px; height: 13px; color: var(--color-text-muted);"></i>
                        <strong style="color: var(--color-text-muted);">${projectWorkOrders.length}</strong> OS
                      </span>
                    </div>

                    <!-- AÇÕES DO PROJETO (REGRA 7) -->
                    <div class="prj-actions-bar">
                      <button class="btn-sub-action ${isSelected ? 'primary' : ''} btn-select-project" data-id="${p.id}" title="Selecionar este projeto para novas unidades">
                        <i data-lucide="${isSelected ? 'check' : 'folder-kanban'}" style="width: 13px; height: 13px;"></i>
                        ${isSelected ? 'Selecionado' : 'Selecionar'}
                      </button>

                      <button class="btn-sub-action success btn-add-unit-direct" data-id="${p.id}" title="Adicionar Unidade/Área a este projeto">
                        <i data-lucide="plus" style="width: 13px; height: 13px;"></i> + Unidade
                      </button>

                      <button class="btn-sub-action btn-view-project-os" data-id="${p.id}" data-client-id="${p.clientId}" title="Ver ${projectWorkOrders.length} Ordens de Serviço deste Projeto na aba de OS" style="color: var(--color-text-muted); border-color: rgba(56, 189, 248, 0.3);">
                        <i data-lucide="file-text" style="width: 13px; height: 13px;"></i> OS (${projectWorkOrders.length})
                      </button>

                      <button class="btn-sub-action primary btn-new-os-for-project" data-id="${p.id}" data-client-id="${p.clientId}" title="Emitir Nova Ordem de Serviço para este Projeto">
                        <i data-lucide="plus" style="width: 13px; height: 13px;"></i> + OS
                      </button>

                      <button class="btn-sub-action btn-view-project" data-id="${p.id}" title="Visualizar Detalhes do Projeto">
                        <i data-lucide="eye" style="width: 13px; height: 13px;"></i>
                      </button>

                      <button class="btn-sub-action btn-edit-project" data-id="${p.id}" title="Editar Projeto">
                        <i data-lucide="edit-3" style="width: 13px; height: 13px;"></i>
                      </button>

                      <button class="btn-sub-action btn-toggle-project-status" data-id="${p.id}" title="${p.status === 'ACTIVE' ? 'Inativar Projeto' : 'Ativar Projeto'}">
                        <i data-lucide="power" style="width: 13px; height: 13px; color: ${p.status === 'ACTIVE' ? 'var(--status-warning-solid)' : '#4ade80'};"></i>
                      </button>

                      <button class="btn-sub-action danger btn-delete-project" data-id="${p.id}" data-name="${this.escapeHtml(p.name)}" title="Excluir Projeto">
                        <i data-lucide="trash-2" style="width: 13px; height: 13px;"></i>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- CORPO EXPANDIDO COM AS UNIDADES/ÁREAS DO PROJETO (REGRA 6) -->
                ${isExpanded ? `
                  <div class="prj-units-tree">
                    <div class="prj-tree-title">
                      <div style="display: flex; align-items: center; gap: 6px;">
                        <i data-lucide="layers" style="width: 14px; height: 14px; color: var(--ia-info-fg, #60a5fa);"></i>
                        <span>UNIDADES / ÁREAS DO PROJETO (${projectUnits.length})</span>
                      </div>
                      <button class="btn-sub-action success btn-add-unit-direct" data-id="${p.id}" style="padding: 3px 8px; font-size: 0.72rem;">
                        <i data-lucide="plus" style="width: 12px; height: 12px;"></i> Nova Unidade
                      </button>
                    </div>

                    ${projectUnits.length === 0 ? `
                      <div style="padding: 20px; background: rgba(255, 255, 255, 0.02); border: 1px dashed var(--ia-line, #3f3f46); border-radius: 6px; text-align: center; color: var(--color-text-disabled);">
                        <p style="margin: 0 0 8px 0; font-size: 0.85rem;">Nenhuma Unidade/Área cadastrada neste projeto.</p>
                        <button class="btn-sub-action success btn-add-unit-direct" data-id="${p.id}">
                          <i data-lucide="plus" style="width: 13px; height: 13px;"></i> + Cadastrar Primeira Unidade/Área
                        </button>
                      </div>
                    ` : `
                      <div style="padding-left: 18px; border-left: 2px solid #27272a; margin-left: 8px;">
                        ${projectUnits.map((u, idx) => {
                          const isLast = idx === projectUnits.length - 1;
                          const branchSymbol = isLast ? '└──' : '├──';
                          const unitEquipments = this.equipments.filter(e => (e.unitAreaId === u.id || e.areaId === u.id));

                          return `
                            <div class="unit-tree-node" data-unit-id="${u.id}">
                              <div class="unit-left-info">
                                <span class="unit-branch-icon" title="Hierarquia">${branchSymbol}</span>
                                <span class="unit-code-pill">${this.escapeHtml(u.code)}</span>
                                <span class="unit-name-text">${this.escapeHtml(u.name)}</span>
                                ${u.description ? `<span class="unit-desc-text">(${this.escapeHtml(u.description)})</span>` : ''}
                                ${this.getUnitStatusBadge(u.status)}
                                <span style="font-size: 0.75rem; background: var(--ia-bg-panel, #27272a); color: var(--color-border-default); padding: 2px 7px; border-radius: 10px; margin-left: 6px;" title="Equipamentos vinculados a esta unidade">
                                  ${unitEquipments.length} ativos
                                </span>
                              </div>

                              <!-- AÇÕES DA UNIDADE/ÁREA (REGRA 8) -->
                              <div class="unit-right-actions">
                                <button class="btn-sub-action btn-view-unit-equipments" data-unit-id="${u.id}" data-project-id="${p.id}" title="Ver equipamentos cadastrados nesta unidade">
                                  <i data-lucide="boxes" style="width: 13px; height: 13px;"></i> Ativos (${unitEquipments.length})
                                </button>

                                <button class="btn-sub-action btn-edit-unit" data-unit-id="${u.id}" title="Editar Unidade/Área">
                                  <i data-lucide="edit-3" style="width: 13px; height: 13px;"></i>
                                </button>

                                <button class="btn-sub-action btn-toggle-unit-status" data-unit-id="${u.id}" title="${u.status === 'ACTIVE' ? 'Inativar Unidade' : 'Ativar Unidade'}">
                                  <i data-lucide="power" style="width: 13px; height: 13px; color: ${u.status === 'ACTIVE' ? 'var(--status-warning-solid)' : '#4ade80'};"></i>
                                </button>

                                <button class="btn-sub-action danger btn-delete-unit" data-unit-id="${u.id}" data-unit-name="${this.escapeHtml(u.name)}" data-eq-count="${unitEquipments.length}" title="Excluir Unidade/Área">
                                  <i data-lucide="trash-2" style="width: 13px; height: 13px;"></i>
                                </button>
                              </div>
                            </div>
                          `;
                        }).join('')}
                      </div>
                    `}
                  </div>
                ` : ''}
              </div>
            `;
          }).join('')}
        </div>

        <div id="prj-modal-host"></div>
        <div id="prj-confirm-host"></div>
      </div>
    `;

    createIcons({
      icons: { 
        FolderKanban, Plus, Search, Filter, Calendar, User, Layers, Eye, Edit3, Trash2, 
        Building2, Clock, CheckCircle2, AlertCircle, ExternalLink, ChevronDown, ChevronRight, 
        GitBranch, Boxes, Power, Link, Check, X 
      },
      nameAttr: "data-lucide",
      root: this
    });

    this.bindEvents();
  }

  private bindEvents() {
    // Busca rápida
    const searchInput = this.querySelector('#prj-search-input') as HTMLInputElement;
    searchInput?.addEventListener('input', (e) => {
      this.searchTerm = (e.target as HTMLInputElement).value;
      this.render();
    });

    // Filtro de Cliente
    this.querySelector('#prj-client-filter')?.addEventListener('change', (e) => {
      this.clientFilter = (e.target as HTMLSelectElement).value;
      this.render();
    });

    // Filtro de Status
    this.querySelector('#prj-status-filter')?.addEventListener('change', (e) => {
      this.statusFilter = (e.target as HTMLSelectElement).value;
      this.render();
    });

    // Expandir Todos / Recolher Todos
    this.querySelector('#btn-expand-all')?.addEventListener('click', () => {
      this.projects.forEach(p => this.expandedProjectIds.add(p.id));
      this.render();
    });

    this.querySelector('#btn-collapse-all')?.addEventListener('click', () => {
      this.expandedProjectIds.clear();
      this.render();
    });

    // Ações de Topo: + Novo Projeto
    this.querySelector('#btn-new-project')?.addEventListener('click', () => {
      this.openProjectModal();
    });
    this.querySelector('#btn-empty-new-prj')?.addEventListener('click', () => {
      this.openProjectModal();
    });

    // Ações de Topo: + Nova Unidade/Área (Respeita Regra 5: se houver projeto selecionado, pré-seleciona)
    this.querySelector('#btn-new-unit-area')?.addEventListener('click', () => {
      this.openUnitAreaModal(this.selectedProjectId || undefined);
    });

    // Botão de adicionar unidade direto no banner de seleção
    this.querySelector('#btn-add-unit-to-selected')?.addEventListener('click', (e) => {
      const pId = (e.currentTarget as HTMLElement).getAttribute('data-id');
      this.openUnitAreaModal(pId || undefined);
    });

    // Limpar seleção
    this.querySelector('#btn-clear-selection')?.addEventListener('click', () => {
      this.selectedProjectId = null;
      this.render();
    });

    // Toggle expandir / recolher card
    this.querySelectorAll('.btn-toggle-expand').forEach(el => {
      el.addEventListener('click', (e) => {
        // Ignora cliques que vieram de botões internos
        if ((e.target as HTMLElement).closest('button')) return;
        const pId = (e.currentTarget as HTMLElement).getAttribute('data-id');
        if (pId) {
          if (this.expandedProjectIds.has(pId)) {
            this.expandedProjectIds.delete(pId);
          } else {
            this.expandedProjectIds.add(pId);
          }
          this.render();
        }
      });
    });

    // Selecionar projeto
    this.querySelectorAll('.btn-select-project').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const pId = (e.currentTarget as HTMLElement).getAttribute('data-id');
        if (pId) {
          this.selectedProjectId = (this.selectedProjectId === pId) ? null : pId;
          this.render();
        }
      });
    });

    // + Unidade direto no projeto
    this.querySelectorAll('.btn-add-unit-direct').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const pId = (e.currentTarget as HTMLElement).getAttribute('data-id');
        this.selectedProjectId = pId;
        this.openUnitAreaModal(pId || undefined);
      });
    });

    // Navegar para a aba de OS geral
    this.querySelector('#btn-goto-work-orders')?.addEventListener('click', () => {
      router.navigate('/management/os');
    });

    // Ver Ordens de Serviço do projeto na aba de OS
    this.querySelectorAll('.btn-view-project-os').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const pId = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const cId = (e.currentTarget as HTMLElement).getAttribute('data-client-id');
        if (pId) {
          router.navigate(`/management/os?projectId=${pId}${cId ? `&clientId=${cId}` : ''}`);
        }
      });
    });

    // Emitir Nova OS para este projeto
    this.querySelectorAll('.btn-new-os-for-project').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const pId = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const cId = (e.currentTarget as HTMLElement).getAttribute('data-client-id');
        if (pId) {
          router.navigate(`/management/os?projectId=${pId}${cId ? `&clientId=${cId}` : ''}&newForProject=${pId}`);
        }
      });
    });

    // Visualizar projeto
    this.querySelectorAll('.btn-view-project').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const pId = (e.currentTarget as HTMLElement).getAttribute('data-id');
        if (pId) router.navigate(`/management/projects/${pId}`);
      });
    });

    // Editar projeto
    this.querySelectorAll('.btn-edit-project').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const pId = (e.currentTarget as HTMLElement).getAttribute('data-id');
        if (pId) {
          const prj = this.projects.find(p => p.id === pId);
          if (prj) this.openProjectModal(prj);
        }
      });
    });

    // Alternar status de projeto (Inativar / Ativar)
    this.querySelectorAll('.btn-toggle-project-status').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const pId = (e.currentTarget as HTMLElement).getAttribute('data-id');
        if (pId) {
          try {
            await window.app.platform.projectService.toggleProjectStatus(pId, sessionRequestContext());
            await this.loadData();
          } catch (err: any) {
            alert(err.safeMessage || err.message || "Erro ao alterar status do projeto.");
          }
        }
      });
    });

    // Excluir projeto
    this.querySelectorAll('.btn-delete-project').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const pId = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const name = (e.currentTarget as HTMLElement).getAttribute('data-name') || '';
        if (pId) this.openDeleteProjectConfirmModal(pId, name);
      });
    });

    // Ações de Unidade: Visualizar Equipamentos
    this.querySelectorAll('.btn-view-unit-equipments').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const pId = (e.currentTarget as HTMLElement).getAttribute('data-project-id');
        const uId = (e.currentTarget as HTMLElement).getAttribute('data-unit-id');
        // Redireciona para equipamentos
        if (pId && uId) {
          router.navigate(`/equipment`);
        }
      });
    });

    // Editar Unidade/Área
    this.querySelectorAll('.btn-edit-unit').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const uId = (e.currentTarget as HTMLElement).getAttribute('data-unit-id');
        if (uId) {
          const unit = this.unitAreas.find(u => u.id === uId);
          if (unit) this.openUnitAreaModal(unit.projectId, unit);
        }
      });
    });

    // Inativar / Ativar Unidade
    this.querySelectorAll('.btn-toggle-unit-status').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const uId = (e.currentTarget as HTMLElement).getAttribute('data-unit-id');
        if (uId) {
          try {
            await window.app.platform.projectService.toggleUnitAreaStatus(uId, sessionRequestContext());
            await this.loadData();
          } catch (err: any) {
            alert(err.safeMessage || err.message || "Erro ao alterar status da unidade.");
          }
        }
      });
    });

    // Excluir Unidade
    this.querySelectorAll('.btn-delete-unit').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const uId = (e.currentTarget as HTMLElement).getAttribute('data-unit-id');
        const name = (e.currentTarget as HTMLElement).getAttribute('data-unit-name') || '';
        const eqCount = Number((e.currentTarget as HTMLElement).getAttribute('data-eq-count') || 0);
        if (uId) this.openDeleteUnitConfirmModal(uId, name, eqCount);
      });
    });

    // Vinculação de áreas pendentes (Regra 13)
    this.querySelectorAll('.btn-execute-bind').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const uId = (e.currentTarget as HTMLElement).getAttribute('data-unit-id');
        const sel = this.querySelector(`.sel-bind-project[data-unit-id="${uId}"]`) as HTMLSelectElement;
        const targetProjectId = sel?.value;
        if (!targetProjectId) {
          alert("Selecione um projeto de destino para vincular esta unidade/área.");
          return;
        }
        try {
          await window.app.platform.projectService.bindUnitAreaToProject(uId!, targetProjectId, sessionRequestContext());
          await this.loadData();
        } catch (err: any) {
          alert(err.safeMessage || err.message || "Erro ao vincular unidade ao projeto.");
        }
      });
    });
  }

  // ----------------------------------------------------
  // MODAL DE CADASTRO / EDIÇÃO DE PROJETO (REGRA 3)
  // ----------------------------------------------------
  /**
   * OS às quais o Projeto está vinculado. Uma OS possui N Projetos e um
   * Projeto pode aparecer em mais de uma OS — daí a lista, não um campo.
   */
  private workOrdersOfProject(projectId: string): WorkOrderOptionLike[] {
    const ids = new Set(
      this.workOrderProjectLinks.filter(l => l.projectId === projectId).map(l => l.workOrderId)
    );
    // O projectId legado da OS continua valendo como vínculo enquanto existir.
    for (const wo of this.workOrders) {
      if ((wo as any).projectId === projectId) ids.add(wo.id);
    }
    return this.workOrders
      .filter(wo => ids.has(wo.id))
      .sort((a, b) => a.code.localeCompare(b.code, 'pt-BR', { numeric: true }));
  }

  /**
   * Histórico que impede trocar Cliente/OS sem o usuário saber.
   * Conta o que a tela realmente tem carregado — não afirma o que não viu.
   */
  private projectHistory(projectId: string): { total: number; parts: string[] } {
    const parts: string[] = [];
    const units = this.unitAreas.filter(u => u.projectId === projectId).length;
    const equipments = this.equipments.filter((e: any) => e.projectId === projectId).length;
    if (units > 0) parts.push(`${units} Unidade(s)/Área(s)`);
    if (equipments > 0) parts.push(`${equipments} equipamento(s)`);
    return { total: units + equipments, parts };
  }

  /**
   * Trocar a OS de um Projeto com histórico exige confirmação explícita —
   * não é uma mudança silenciosa. O histórico não se move com o vínculo.
   */
  private confirmWorkOrderChange(
    project: Project,
    nextWorkOrderId: string,
    history: { total: number; parts: string[] }
  ): Promise<boolean> {
    const host = this.querySelector('#prj-confirm-host') as HTMLElement | null;
    if (!host) return Promise.resolve(false);

    const nextWorkOrder = this.workOrders.find(wo => wo.id === nextWorkOrderId);
    const currentCodes = this.workOrdersOfProject(project.id).map(wo => wo.code).join(', ') || '—';

    return new Promise<boolean>(resolve => {
      host.innerHTML = `
        <div class="modal-backdrop" style="position: fixed; inset: 0; background: rgba(0,0,0,0.88); z-index: 1100; display: flex; align-items: center; justify-content: center; padding: 20px;">
          <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 10px; width: 100%; max-width: 480px; padding: 22px;">
            <h3 style="margin: 0 0 12px 0; font-size: 1.05rem; color: var(--color-text-primary);">Trocar a Ordem de Serviço deste Projeto?</h3>
            <p style="margin: 0 0 10px 0; font-size: 0.85rem; color: var(--color-graphite-300); line-height: 1.5;">
              ${this.escapeHtml(project.code)} — ${this.escapeHtml(project.name)} sairá de
              <strong style="color: var(--color-text-primary);">${this.escapeHtml(currentCodes)}</strong>
              para <strong style="color: var(--color-text-primary);">${this.escapeHtml(nextWorkOrder?.code || nextWorkOrderId)}</strong>.
            </p>
            <p style="margin: 0 0 18px 0; font-size: 0.85rem; color: var(--ia-warning-fg, #facc15); line-height: 1.5;">
              O Projeto já possui ${this.escapeHtml(history.parts.join(', '))}. Esse histórico permanece vinculado ao Projeto e não é transferido.
            </p>
            <div style="display: flex; justify-content: flex-end; gap: 10px;">
              <button type="button" id="btn-cancel-wo-change" style="background: var(--ia-bg-panel, #27272a); color: var(--color-text-primary); border: 1px solid var(--color-input-border); padding: 9px 18px; border-radius: 6px; cursor: pointer; font-size: 0.875rem;">Cancelar</button>
              <button type="button" id="btn-confirm-wo-change" style="background: #ca8a04; color: #18181b; border: none; padding: 9px 18px; border-radius: 6px; cursor: pointer; font-size: 0.875rem; font-weight: 700;">Confirmar troca</button>
            </div>
          </div>
        </div>`;

      const finish = (answer: boolean) => {
        host.innerHTML = '';
        resolve(answer);
      };

      host.querySelector('#btn-cancel-wo-change')?.addEventListener('click', () => finish(false));
      host.querySelector('#btn-confirm-wo-change')?.addEventListener('click', () => finish(true));
    });
  }

  private async openProjectModal(project?: Project): Promise<void> {
    const modalContainer = this.querySelector('#prj-modal-host') as HTMLElement;
    if (!modalContainer) return;

    const isEdit = !!project;
    // Novo Projeto começa sem Cliente: a escolha é do usuário, não do contexto
    // global. Na edição, o Cliente é o que já está gravado.
    const defaultClientId = project?.clientId || '';
    const clientOptions = isEdit ? this.clients : clientOptionsForNewProject(this.clients);
    const defaultWorkOrderId = project ? this.workOrdersOfProject(project.id)[0]?.id || '' : '';
    const history = project ? this.projectHistory(project.id) : null;

    let allEngineers: any[] = [];
    try {
      const dummyCtx = { activeClientId: contextStore.get().activeClientId, permissions: contextStore.get().permissions, correlationId: '', userId: contextStore.get().userId } as any;
      const profs = await window.app.platform.adminService.listProfessionals(dummyCtx).catch(() => []);
      // Filtra apenas profissionais do tipo ENGINEER/PLH ativos
      allEngineers = profs.filter((p: any) =>
        p.status === 'ACTIVE' &&
        (p.professionalType === 'ENGINEER' ||
         p.functionTitle?.toLowerCase().includes('eng') ||
         p.functionTitle?.toLowerCase().includes('plh'))
      );
    } catch {}

    // Engenheiro Responsável do Projeto. O PLH responsável não é escolhido
    // aqui: ele pertence ao cadastro do Cliente.
    const currentEngineers = allEngineers.filter(e => (e.plhClientId || e.clientId) === defaultClientId);

    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.85); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 10px; width: 100%; max-width: 640px; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7);">
          <div style="padding: 16px 20px; border-bottom: 1px solid var(--ia-line, #27272a); display: flex; justify-content: space-between; align-items: center; background: var(--ia-bg-panel, #202024);">
            <h3 style="margin: 0; font-size: 1.15rem; color: var(--color-text-primary); display: flex; align-items: center; gap: 8px;">
              <i data-lucide="folder-kanban" style="color: var(--ia-info-fg, #60a5fa); width: 20px; height: 20px;"></i>
              ${isEdit ? 'Editar Projeto' : 'Novo Projeto'}
            </h3>
            <button id="btn-close-prj-modal" style="background: transparent; border: none; color: var(--color-text-disabled); font-size: 1.2rem; cursor: pointer;">✕</button>
          </div>

          <form id="form-project" style="padding: 22px; display: flex; flex-direction: column; gap: 14px;">
            <div id="prj-error-banner" style="display: none; background: rgba(239, 68, 68, 0.15); border: 1px solid var(--status-danger-solid); color: var(--status-danger-border); padding: 10px 14px; border-radius: 6px; font-size: 0.85rem;"></div>

            <!-- 1º CLIENTE: a hierarquia começa nele -->
            <div>
              <label style="display: block; font-size: 0.82rem; color: var(--color-graphite-300); margin-bottom: 5px; font-weight: 600;">Cliente *</label>
              <select id="prj-client-select" name="clientId" required ${isEdit ? 'disabled' : ''} style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 9px 12px; color: var(--color-text-primary);">
                <option value="">Selecione o Cliente...</option>
                ${clientOptions.map(c => `<option value="${c.id}" ${defaultClientId === c.id ? 'selected' : ''}>${this.escapeHtml(formatClientLabel(c))}</option>`).join('')}
              </select>
              ${isEdit ? '<input type="hidden" name="clientId" value="' + defaultClientId + '">' : ''}
            </div>

            <!-- 2º ORDEM DE SERVIÇO: só as do Cliente escolhido -->
            <div>
              <label style="display: block; font-size: 0.82rem; color: var(--color-graphite-300); margin-bottom: 5px; font-weight: 600;">Ordem de Serviço *</label>
              <select id="prj-work-order-select" name="workOrderId" required style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 9px 12px; color: var(--color-text-primary);"></select>
              <div id="prj-work-order-hint" style="display: none; margin-top: 6px; font-size: 0.78rem; color: var(--color-text-disabled); align-items: center; gap: 8px;">
                <span id="prj-work-order-hint-text"></span>
                <button type="button" id="btn-prj-new-os" style="background: transparent; border: none; color: var(--ia-info-fg, #60a5fa); text-decoration: underline; cursor: pointer; font-size: 0.78rem; padding: 0;">Cadastrar OS</button>
              </div>
            </div>

            ${history && history.total > 0 ? `
              <div style="background: rgba(234, 179, 8, 0.12); border: 1px solid rgba(234, 179, 8, 0.5); color: var(--ia-warning-fg, #facc15); padding: 10px 14px; border-radius: 6px; font-size: 0.8rem;">
                Este Projeto já possui histórico (${history.parts.join(', ')}). Trocar a Ordem de Serviço mantém o histórico onde está e exige confirmação.
              </div>` : ''}

            <!-- CÓDIGO E NOME -->
            <div style="display: grid; grid-template-columns: 120px 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.82rem; color: var(--color-graphite-300); margin-bottom: 5px; font-weight: 600;">Código *</label>
                <input type="text" name="code" required value="${this.escapeHtml(project?.code || '')}" placeholder="PRJ-01" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 9px 12px; color: var(--color-text-primary); text-transform: uppercase;">
              </div>
              <div>
                <label style="display: block; font-size: 0.82rem; color: var(--color-graphite-300); margin-bottom: 5px; font-weight: 600;">Nome do Projeto *</label>
                <input type="text" name="name" required value="${this.escapeHtml(project?.name || '')}" placeholder="Ex: Parada de Manutenção 2026" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 9px 12px; color: var(--color-text-primary);">
              </div>
            </div>

            <!-- DESCRIÇÃO -->
            <div>
              <label style="display: block; font-size: 0.82rem; color: var(--color-text-disabled); margin-bottom: 5px;">Descrição do Projeto</label>
              <textarea name="description" rows="2" placeholder="Escopo, detalhamento e finalidade das atividades..." style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 9px 12px; color: var(--color-text-primary); resize: vertical;">${this.escapeHtml(project?.description || '')}</textarea>
            </div>

            <!-- STATUS E RESPONSÁVEL -->
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.82rem; color: var(--color-graphite-300); margin-bottom: 5px; font-weight: 600;">Status do Projeto</label>
                <select name="status" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 9px 12px; color: var(--color-text-primary);">
                  <option value="ACTIVE" ${project?.status === 'ACTIVE' ? 'selected' : ''}>Ativo</option>
                  <option value="PLANNING" ${project?.status === 'PLANNING' ? 'selected' : ''}>Em Planejamento</option>
                  <option value="COMPLETED" ${project?.status === 'COMPLETED' ? 'selected' : ''}>Concluído</option>
                  <option value="CANCELLED" ${project?.status === 'CANCELLED' ? 'selected' : ''}>Cancelado / Inativo</option>
                </select>
              </div>
              <div>
                <label style="display: block; font-size: 0.82rem; color: var(--color-graphite-300); margin-bottom: 5px; font-weight: 600;">Engenheiro Responsável</label>
                <select id="prj-engineer-select" name="engineerProfessionalId" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 9px 12px; color: var(--color-text-primary);">
                  <option value="">Selecionar…</option>
                  ${currentEngineers.map(e => `<option value="${e.id}" ${project?.engineerProfessionalId === e.id ? 'selected' : ''}>${this.escapeHtml(e.name)}</option>`).join('')}
                </select>
                <input type="hidden" name="manager" id="prj-manager-hidden" value="${this.escapeHtml(project?.manager || '')}">
              </div>
            </div>

            <!-- DATAS DE INÍCIO E PREVISÃO DE TÉRMINO (CURVA S) -->
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.82rem; color: var(--color-graphite-300); margin-bottom: 5px; font-weight: 600;">Data de Início</label>
                <input type="date" name="startDate" value="${this.escapeHtml(project?.startDate ? project.startDate.slice(0, 10) : '')}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 9px 12px; color: var(--color-text-primary);">
              </div>
              <div>
                <label style="display: block; font-size: 0.82rem; color: var(--color-graphite-300); margin-bottom: 5px; font-weight: 600;">Previsão de Término</label>
                <input type="date" name="expectedEndDate" value="${this.escapeHtml(project?.expectedEndDate ? project.expectedEndDate.slice(0, 10) : '')}" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 9px 12px; color: var(--color-text-primary);">
              </div>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 10px; margin-top: 14px; border-top: 1px solid #27272a; padding-top: 16px;">
              <button type="button" id="btn-cancel-prj-modal" style="background: var(--ia-bg-panel, #27272a); color: var(--color-text-primary); border: 1px solid var(--color-input-border); padding: 9px 18px; border-radius: 6px; cursor: pointer; font-size: 0.875rem;">Cancelar</button>
              <button type="submit" class="btn-new-prj">Salvar Projeto</button>
            </div>
          </form>
        </div>
      </div>
    `;

    createIcons({
      icons: { FolderKanban },
      nameAttr: "data-lucide",
      root: modalContainer
    });

    const closeModal = () => {
      modalContainer.innerHTML = '';
    };

    modalContainer.querySelector('#btn-close-prj-modal')?.addEventListener('click', closeModal);
    modalContainer.querySelector('#btn-cancel-prj-modal')?.addEventListener('click', closeModal);

    const clientSelect = modalContainer.querySelector('#prj-client-select') as HTMLSelectElement;
    const engineerSelect = modalContainer.querySelector('#prj-engineer-select') as HTMLSelectElement;
    const workOrderSelect = modalContainer.querySelector('#prj-work-order-select') as HTMLSelectElement;
    const workOrderHint = modalContainer.querySelector('#prj-work-order-hint') as HTMLElement;
    const workOrderHintText = modalContainer.querySelector('#prj-work-order-hint-text') as HTMLElement;
    const newOsButton = modalContainer.querySelector('#btn-prj-new-os') as HTMLButtonElement;

    /**
     * Redesenha a OS a partir do Cliente escolhido.
     * Sem Cliente o campo fica desabilitado; com Cliente sem OS, o usuário
     * recebe o motivo e um caminho, não um select vazio.
     */
    const refreshWorkOrders = (selectedClientId: string, keepWorkOrderId: string) => {
      const options = workOrderOptionsForClient(selectedClientId, this.workOrders);
      const kept = keepWorkOrderOnClientChange(keepWorkOrderId, options);

      if (!selectedClientId) {
        workOrderSelect.disabled = true;
        workOrderSelect.innerHTML = `<option value="">${SELECT_CLIENT_FIRST_PLACEHOLDER}</option>`;
        workOrderHint.style.display = 'none';
        return;
      }

      if (options.length === 0) {
        workOrderSelect.disabled = true;
        workOrderSelect.innerHTML = '<option value="">Nenhuma OS disponível</option>';
        workOrderHintText.textContent = NO_WORK_ORDERS_FOR_CLIENT_MESSAGE;
        workOrderHint.style.display = 'flex';
        newOsButton.style.display = '';
        newOsButton.dataset['clientId'] = selectedClientId;
        return;
      }

      workOrderSelect.disabled = false;
      workOrderSelect.innerHTML = '<option value="">Selecione a Ordem de Serviço...</option>' +
        options.map(wo => `<option value="${wo.id}" ${kept === wo.id ? 'selected' : ''}>${this.escapeHtml(formatWorkOrderOptionLabel(wo))}</option>`).join('');
      workOrderHint.style.display = 'none';
    };

    // Edição carrega na ordem da hierarquia: Cliente, depois as OS dele,
    // depois a OS atual.
    refreshWorkOrders(defaultClientId, defaultWorkOrderId);

    newOsButton?.addEventListener('click', () => {
      const clientId = newOsButton.dataset['clientId'] || clientSelect?.value || '';
      router.navigate(`/management/os${clientId ? `?clientId=${clientId}` : ''}`);
    });

    clientSelect?.addEventListener('change', (e) => {
      const selectedClientId = (e.target as HTMLSelectElement).value;

      // Trocar o Cliente não pode deixar para trás uma OS do Cliente anterior.
      refreshWorkOrders(selectedClientId, workOrderSelect?.value || '');

      // Engenheiros do Cliente selecionado. O PLH não é do Projeto: é
      // definido no cadastro do Cliente e vale para todos os seus Projetos.
      const filteredEngineers = allEngineers.filter(eng =>
        (eng.plhClientId || eng.clientId) === selectedClientId
      );

      engineerSelect.innerHTML = '<option value="">Selecionar…</option>' +
        filteredEngineers.map(eng => `<option value="${eng.id}">${this.escapeHtml(eng.name)}</option>`).join('');
    });

    const form = modalContainer.querySelector('#form-project') as HTMLFormElement;
    const errorBanner = modalContainer.querySelector('#prj-error-banner') as HTMLElement;

    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (errorBanner) errorBanner.style.display = 'none';

      const formData = new FormData(form);
      const code = (formData.get('code') as string)?.trim();
      const name = (formData.get('name') as string)?.trim();
      const clientId = (formData.get('clientId') as string)?.trim() || defaultClientId;
      const description = (formData.get('description') as string)?.trim();
      const status = (formData.get('status') as ProjectStatus) || 'ACTIVE';
      const manager = (formData.get('manager') as string)?.trim();
      const startDate = (formData.get('startDate') as string)?.trim() || null;
      const expectedEndDate = (formData.get('expectedEndDate') as string)?.trim() || null;

      if (!code || !name) {
        if (errorBanner) {
          errorBanner.textContent = "Preencha o código e o nome do projeto.";
          errorBanner.style.display = 'block';
        }
        return;
      }

      if (!clientId) {
        if (errorBanner) {
          errorBanner.textContent = "Selecione o Cliente.";
          errorBanner.style.display = 'block';
        }
        return;
      }

      const workOrderId = (formData.get('workOrderId') as string)?.trim() || '';
      if (!workOrderId) {
        if (errorBanner) {
          errorBanner.textContent = "Selecione a Ordem de Serviço do Projeto.";
          errorBanner.style.display = 'block';
        }
        return;
      }

      // Engenheiro Responsável é opcional (CLIENTE → OS → PROJETO). O PLH NÃO
      // existe no Projeto: é definido no cadastro do Cliente e só é exigido no
      // gate de fechamento/aprovação/assinatura de cada documento técnico.
      const engineerProfessionalId = (formData.get('engineerProfessionalId') as string)?.trim() || null;
      const ctx = contextStore.get();
      const requestContext = {
        activeClientId: clientId,
        permissions: ctx.permissions,
        correlationId: '',
        userId: ctx.userId,
        professionalId: ctx.professionalId || null
      } as any;

      const submitButton = form.querySelector("button[type='submit']") as HTMLButtonElement | null;
      if (submitButton) submitButton.disabled = true;

      try {
        // A OS é conferida ANTES de gravar: descobrir a inconsistência depois
        // deixaria um Projeto criado sem a OS que o motivou.
        await window.app.platform.rdoService.assertWorkOrderBelongsToClient(requestContext, workOrderId, clientId);

        if (isEdit && project) {
          const previousWorkOrderIds = this.workOrdersOfProject(project.id).map(wo => wo.id);
          const changedWorkOrder = !previousWorkOrderIds.includes(workOrderId);

          if (changedWorkOrder && (history?.total ?? 0) > 0) {
            const confirmed = await this.confirmWorkOrderChange(project, workOrderId, history!);
            if (!confirmed) {
              if (submitButton) submitButton.disabled = false;
              return;
            }
          }

          await window.app.platform.projectService.updateProject(project.id, {
            code,
            name,
            description,
            status,
            manager,
            startDate,
            expectedEndDate,
            engineerProfessionalId
          }, requestContext);

          if (changedWorkOrder) {
            await window.app.platform.rdoService.linkProjectToWorkOrder(requestContext, workOrderId, project.id);
            // Desvincular a OS anterior só quando ela não tem Sub-OS deste
            // Projeto; o serviço recusa o resto e o erro é mostrado.
            for (const previousId of previousWorkOrderIds) {
              await window.app.platform.rdoService.unlinkProjectFromWorkOrder(requestContext, previousId, project.id);
            }
          }
        } else {
          const created = await window.app.platform.projectService.createProject({
            code,
            name,
            clientId,
            description,
            status,
            manager,
            startDate,
            expectedEndDate,
            engineerProfessionalId
          }, requestContext);

          // O Projeto nasce dentro da OS: 1 OS → N Projetos, pela entidade de
          // vínculo, nunca voltando ao workOrder.projectId único.
          await window.app.platform.rdoService.linkProjectToWorkOrder(requestContext, workOrderId, created.id);

          // Já seleciona e expande o projeto criado
          this.selectedProjectId = created.id;
          this.expandedProjectIds.add(created.id);
        }
        closeModal();
        await this.loadData();
      } catch (err: any) {
        if (submitButton) submitButton.disabled = false;
        if (errorBanner) {
          errorBanner.textContent = err.safeMessage || err.message || "Erro ao salvar projeto.";
          errorBanner.style.display = 'block';
        }
      }
    });
  }

  // ----------------------------------------------------
  // MODAL DE NOVA / EDIÇÃO UNIDADE/ÁREA (REGRAS 4 E 5)
  // ----------------------------------------------------
  private openUnitAreaModal(presetProjectId?: string, unit?: ProjectUnitArea): void {
    const modalContainer = this.querySelector('#prj-modal-host') as HTMLElement;
    if (!modalContainer) return;

    const isEdit = !!unit;
    // Projetos disponíveis respeitando o cliente filtrado se aplicável
    const availableProjects = this.projects.filter(p => this.clientFilter === 'ALL' || p.clientId === this.clientFilter);
    const selectedProjId = unit?.projectId || presetProjectId || this.selectedProjectId || (availableProjects[0]?.id || '');

    if (availableProjects.length === 0) {
      alert("Nenhum projeto cadastrado para este cliente. Cadastre pelo menos um Projeto antes de criar Unidades/Áreas.");
      this.openProjectModal();
      return;
    }

    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.85); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 10px; width: 100%; max-width: 580px; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7);">
          <div style="padding: 16px 20px; border-bottom: 1px solid var(--ia-line, #27272a); display: flex; justify-content: space-between; align-items: center; background: var(--ia-bg-panel, #202024);">
            <h3 style="margin: 0; font-size: 1.15rem; color: var(--color-text-primary); display: flex; align-items: center; gap: 8px;">
              <i data-lucide="layers" style="color: var(--status-success-text); width: 20px; height: 20px;"></i>
              ${isEdit ? 'Editar Unidade / Área' : 'Nova Unidade / Área'}
            </h3>
            <button id="btn-close-unit-modal" style="background: transparent; border: none; color: var(--color-text-disabled); font-size: 1.2rem; cursor: pointer;">✕</button>
          </div>

          <form id="form-unit-area" style="padding: 22px; display: flex; flex-direction: column; gap: 14px;">
            <div id="unit-error-banner" style="display: none; background: rgba(239, 68, 68, 0.15); border: 1px solid var(--status-danger-solid); color: var(--status-danger-border); padding: 10px 14px; border-radius: 6px; font-size: 0.85rem;"></div>

            <!-- REGRA 4 E 5: PROJETO VINCULADO OBRIGATÓRIO E PRÉ-PREENCHIDO -->
            <div>
              <label style="display: block; font-size: 0.82rem; color: var(--color-graphite-300); margin-bottom: 5px; font-weight: 600;">
                Projeto Vinculado *
                <span style="font-weight: 400; color: var(--color-graphite-400); font-size: 0.75rem;">(Obrigatório — nenhuma unidade existe sem projeto)</span>
              </label>
              <select name="projectId" id="modal-unit-project-select" required style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 9px 12px; color: var(--color-text-primary);">
                <option value="">Selecione o Projeto...</option>
                ${availableProjects.map(p => `
                  <option value="${p.id}" ${selectedProjId === p.id ? 'selected' : ''}>
                    ${this.escapeHtml(p.code)} - ${this.escapeHtml(p.name)}
                  </option>
                `).join('')}
              </select>
            </div>

            <!-- CÓDIGO E NOME DA UNIDADE/ÁREA -->
            <div style="display: grid; grid-template-columns: 1fr 2fr; gap: 12px;">
              <div>
                <label style="display: block; font-size: 0.82rem; color: var(--color-graphite-300); margin-bottom: 5px; font-weight: 600;">Código *</label>
                <input type="text" name="code" required value="${this.escapeHtml(unit?.code || '')}" placeholder="Ex: U-01, UT-100" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 9px 12px; color: var(--color-text-primary); font-family: monospace; font-weight: 600;">
              </div>
              <div>
                <label style="display: block; font-size: 0.82rem; color: var(--color-graphite-300); margin-bottom: 5px; font-weight: 600;">Nome da Unidade/Área *</label>
                <input type="text" name="name" required value="${this.escapeHtml(unit?.name || '')}" placeholder="Ex: Área de Utilidades, Destilação" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 9px 12px; color: var(--color-text-primary);">
              </div>
            </div>

            <!-- DESCRIÇÃO -->
            <div>
              <label style="display: block; font-size: 0.82rem; color: var(--color-text-disabled); margin-bottom: 5px;">Descrição</label>
              <textarea name="description" rows="2" placeholder="Localização física, equipamentos ou utilidades instaladas..." style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 9px 12px; color: var(--color-text-primary); resize: vertical;">${this.escapeHtml(unit?.description || '')}</textarea>
            </div>

            <!-- STATUS -->
            <div>
              <label style="display: block; font-size: 0.82rem; color: var(--color-graphite-300); margin-bottom: 5px; font-weight: 600;">Status</label>
              <select name="status" style="width: 100%; background: var(--color-input-bg); border: 1px solid var(--color-input-border); border-radius: 6px; padding: 9px 12px; color: var(--color-text-primary);">
                <option value="ACTIVE" ${unit?.status === 'ACTIVE' ? 'selected' : ''}>Ativo</option>
                <option value="INACTIVE" ${unit?.status === 'INACTIVE' ? 'selected' : ''}>Inativo</option>
              </select>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 10px; margin-top: 14px; border-top: 1px solid #27272a; padding-top: 16px;">
              <button type="button" id="btn-cancel-unit-modal" style="background: var(--ia-bg-panel, #27272a); color: var(--color-text-primary); border: 1px solid var(--color-input-border); padding: 9px 18px; border-radius: 6px; cursor: pointer; font-size: 0.875rem;">Cancelar</button>
              <button type="submit" class="btn-new-unit">Salvar Unidade/Área</button>
            </div>
          </form>
        </div>
      </div>
    `;

    createIcons({
      icons: { Layers },
      nameAttr: "data-lucide",
      root: modalContainer
    });

    const closeModal = () => {
      modalContainer.innerHTML = '';
    };

    modalContainer.querySelector('#btn-close-unit-modal')?.addEventListener('click', closeModal);
    modalContainer.querySelector('#btn-cancel-unit-modal')?.addEventListener('click', closeModal);

    const form = modalContainer.querySelector('#form-unit-area') as HTMLFormElement;
    const errorBanner = modalContainer.querySelector('#unit-error-banner') as HTMLElement;

    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (errorBanner) errorBanner.style.display = 'none';

      const formData = new FormData(form);
      const projectId = (formData.get('projectId') as string)?.trim();
      const code = (formData.get('code') as string)?.trim();
      const name = (formData.get('name') as string)?.trim();
      const description = (formData.get('description') as string)?.trim();
      const status = (formData.get('status') as UnitAreaStatus) || 'ACTIVE';

      if (!projectId) {
        if (errorBanner) {
          errorBanner.textContent = "Obrigatório vincular a um Projeto existente.";
          errorBanner.style.display = 'block';
        }
        return;
      }

      if (!code || !name) {
        if (errorBanner) {
          errorBanner.textContent = "Preencha o código e o nome da Unidade/Área.";
          errorBanner.style.display = 'block';
        }
        return;
      }

      try {
        if (isEdit && unit) {
          await window.app.platform.projectService.updateUnitArea(unit.id, {
            projectId,
            code,
            name,
            description,
            status
          }, sessionRequestContext());
        } else {
          await window.app.platform.projectService.createUnitArea({
            projectId,
            code,
            name,
            description,
            status
          }, sessionRequestContext());
        }

        // Garante que o projeto fique expandido
        this.expandedProjectIds.add(projectId);
        this.selectedProjectId = projectId;

        closeModal();
        await this.loadData();
      } catch (err: any) {
        if (errorBanner) {
          errorBanner.textContent = err.safeMessage || err.message || "Erro ao salvar Unidade/Área.";
          errorBanner.style.display = 'block';
        }
      }
    });
  }

  // ----------------------------------------------------
  // CONFIRMAÇÃO DE EXCLUSÃO DE PROJETO
  // ----------------------------------------------------
  private openDeleteProjectConfirmModal(id: string, name: string): void {
    const modalContainer = this.querySelector('#prj-modal-host') as HTMLElement;
    if (!modalContainer) return;

    const prjUnits = this.unitAreas.filter(u => u.projectId === id);
    const prjEquipments = this.equipments.filter(e => e.projectId === id);

    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.85); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 10px; width: 100%; max-width: 480px; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7); text-align: center; padding: 24px;">
          <div style="width: 48px; height: 48px; border-radius: 50%; background: rgba(239, 68, 68, 0.15); color: var(--status-danger-solid); border: 1px solid rgba(239, 68, 68, 0.3); display: flex; align-items: center; justify-content: center; margin: 0 auto 16px auto;">
            <i data-lucide="trash-2" style="width: 24px; height: 24px;"></i>
          </div>
          <h3 style="margin: 0 0 10px 0; font-size: 1.2rem; font-weight: 700; color: var(--color-text-primary);">Excluir Projeto</h3>
          <p style="color: var(--color-text-disabled); font-size: 0.9rem; margin: 0 0 16px 0; line-height: 1.5;">
            Tem certeza que deseja excluir o projeto <strong style="color: var(--color-text-primary);">"${this.escapeHtml(name)}"</strong>?
          </p>

          ${prjEquipments.length > 0 ? `
            <div style="background: rgba(239, 68, 68, 0.15); border: 1px solid var(--status-danger-solid); color: var(--status-danger-border); padding: 12px 14px; border-radius: 6px; font-size: 0.8125rem; margin-bottom: 16px; text-align: left; display: flex; align-items: flex-start; gap: 8px;">
              <i data-lucide="alert-circle" style="width: 16px; height: 16px; flex-shrink: 0; margin-top: 2px;"></i>
              <div>
                <strong>Atenção Regra de Integridade:</strong> Existem <strong>${prjEquipments.length} equipamento(s)</strong> cadastrados vinculados a este projeto. Recomenda-se <strong>Inativar</strong> o projeto para preservar o histórico técnico de inspeções.
              </div>
            </div>
          ` : prjUnits.length > 0 ? `
            <div style="background: rgba(234, 179, 8, 0.12); border: 1px solid var(--status-warning-border); color: var(--status-warning-border); padding: 10px 14px; border-radius: 6px; font-size: 0.8125rem; margin-bottom: 16px; text-align: left; display: flex; align-items: flex-start; gap: 8px;">
              <i data-lucide="alert-circle" style="width: 16px; height: 16px; flex-shrink: 0; margin-top: 2px;"></i>
              <span>Este projeto possui <strong>${prjUnits.length} unidade(s)/área(s)</strong> associadas que também serão removidas.</span>
            </div>
          ` : ''}

          <div id="prj-delete-error" style="display: none; background: rgba(239, 68, 68, 0.15); border: 1px solid var(--status-danger-solid); color: var(--status-danger-border); padding: 8px 12px; border-radius: 6px; font-size: 0.82rem; margin-bottom: 14px; text-align: left;"></div>

          <div style="display: flex; justify-content: center; gap: 12px; margin-top: 8px;">
            <button type="button" id="btn-cancel-del-prj" style="background: var(--ia-bg-panel, #27272a); color: var(--color-text-primary); border: 1px solid var(--color-input-border); padding: 8px 20px; border-radius: 6px; cursor: pointer; font-size: 0.875rem; font-weight: 500;">Cancelar</button>
            <button type="button" id="btn-confirm-del-prj" style="background: var(--status-danger-solid); color: var(--color-text-primary); border: none; padding: 8px 20px; border-radius: 6px; cursor: pointer; font-size: 0.875rem; font-weight: 600; display: inline-flex; align-items: center; gap: 6px;">
              <i data-lucide="trash-2" style="width: 14px; height: 14px;"></i>
              <span>Sim, Excluir</span>
            </button>
          </div>
        </div>
      </div>
    `;

    createIcons({
      icons: { Trash2, AlertCircle },
      nameAttr: "data-lucide",
      root: modalContainer
    });

    const closeDelModal = () => {
      modalContainer.innerHTML = '';
    };

    modalContainer.querySelector('#btn-cancel-del-prj')?.addEventListener('click', closeDelModal);

    modalContainer.querySelector('#btn-confirm-del-prj')?.addEventListener('click', async () => {
      const errorDiv = modalContainer.querySelector('#prj-delete-error') as HTMLElement;
      if (errorDiv) errorDiv.style.display = 'none';

      try {
        await window.app.platform.projectService.deleteProject(id, sessionRequestContext());
        if (this.selectedProjectId === id) this.selectedProjectId = null;
        closeDelModal();
        await this.loadData();
      } catch (err: any) {
        if (errorDiv) {
          errorDiv.textContent = err.safeMessage || err.message || "Erro ao excluir projeto.";
          errorDiv.style.display = 'block';
        }
      }
    });
  }

  // ----------------------------------------------------
  // CONFIRMAÇÃO DE EXCLUSÃO DE UNIDADE/ÁREA
  // ----------------------------------------------------
  private openDeleteUnitConfirmModal(id: string, name: string, eqCount: number): void {
    const modalContainer = this.querySelector('#prj-modal-host') as HTMLElement;
    if (!modalContainer) return;

    modalContainer.innerHTML = `
      <div class="modal-backdrop" style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.85); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 20px;">
        <div style="background: var(--ia-bg-panel, #18181b); border: 1px solid var(--color-input-border); border-radius: 10px; width: 100%; max-width: 480px; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7); text-align: center; padding: 24px;">
          <div style="width: 48px; height: 48px; border-radius: 50%; background: rgba(239, 68, 68, 0.15); color: var(--status-danger-solid); border: 1px solid rgba(239, 68, 68, 0.3); display: flex; align-items: center; justify-content: center; margin: 0 auto 16px auto;">
            <i data-lucide="trash-2" style="width: 24px; height: 24px;"></i>
          </div>
          <h3 style="margin: 0 0 10px 0; font-size: 1.2rem; font-weight: 700; color: var(--color-text-primary);">Excluir Unidade / Área</h3>
          <p style="color: var(--color-text-disabled); font-size: 0.9rem; margin: 0 0 16px 0; line-height: 1.5;">
            Tem certeza que deseja excluir a Unidade/Área <strong style="color: var(--color-text-primary);">"${this.escapeHtml(name)}"</strong>?
          </p>

          ${eqCount > 0 ? `
            <div style="background: rgba(239, 68, 68, 0.15); border: 1px solid var(--status-danger-solid); color: var(--status-danger-border); padding: 12px 14px; border-radius: 6px; font-size: 0.8125rem; margin-bottom: 16px; text-align: left; display: flex; align-items: flex-start; gap: 8px;">
              <i data-lucide="alert-circle" style="width: 16px; height: 16px; flex-shrink: 0; margin-top: 2px;"></i>
              <div>
                <strong>Atenção Regra de Integridade:</strong> Existem <strong>${eqCount} equipamento(s)</strong> vinculados a esta Unidade/Área. Excluir fisicamente a unidade deixará esses ativos órfãos. Recomenda-se <strong>Inativar</strong> a unidade.
              </div>
            </div>
          ` : ''}

          <div id="unit-delete-error" style="display: none; background: rgba(239, 68, 68, 0.15); border: 1px solid var(--status-danger-solid); color: var(--status-danger-border); padding: 8px 12px; border-radius: 6px; font-size: 0.82rem; margin-bottom: 14px; text-align: left;"></div>

          <div style="display: flex; justify-content: center; gap: 12px; margin-top: 8px;">
            <button type="button" id="btn-cancel-del-unit" style="background: var(--ia-bg-panel, #27272a); color: var(--color-text-primary); border: 1px solid var(--color-input-border); padding: 8px 20px; border-radius: 6px; cursor: pointer; font-size: 0.875rem; font-weight: 500;">Cancelar</button>
            <button type="button" id="btn-confirm-del-unit" style="background: var(--status-danger-solid); color: var(--color-text-primary); border: none; padding: 8px 20px; border-radius: 6px; cursor: pointer; font-size: 0.875rem; font-weight: 600; display: inline-flex; align-items: center; gap: 6px;">
              <i data-lucide="trash-2" style="width: 14px; height: 14px;"></i>
              <span>Sim, Excluir</span>
            </button>
          </div>
        </div>
      </div>
    `;

    createIcons({
      icons: { Trash2, AlertCircle },
      nameAttr: "data-lucide",
      root: modalContainer
    });

    const closeDelModal = () => {
      modalContainer.innerHTML = '';
    };

    modalContainer.querySelector('#btn-cancel-del-unit')?.addEventListener('click', closeDelModal);

    modalContainer.querySelector('#btn-confirm-del-unit')?.addEventListener('click', async () => {
      const errorDiv = modalContainer.querySelector('#unit-delete-error') as HTMLElement;
      if (errorDiv) errorDiv.style.display = 'none';

      try {
        await window.app.platform.projectService.deleteUnitArea(id, sessionRequestContext());
        closeDelModal();
        await this.loadData();
      } catch (err: any) {
        if (errorDiv) {
          errorDiv.textContent = err.safeMessage || err.message || "Erro ao excluir Unidade/Área.";
          errorDiv.style.display = 'block';
        }
      }
    });
  }
}

if (!customElements.get('app-projects-page')) {
  customElements.define('app-projects-page', AppProjectsPage);
}



